import {
  Box,
  Flex,
  Input,
  type RuleSearchResult as ManagedRuleResult,
  RuleSearchResults,
  Spinner,
} from '@rocket/ui';
import { useCallback, useEffect, useState } from 'react';
import { useFtueHost } from '../host';
import type { RuleSearchResult } from '../onboarding-api';
import { searchRules } from '../onboarding-api';

/** Convert onboarding API RuleSearchResult to managed RuleSearchResult for RuleSearchCard */
function convertToManagedResult(result: RuleSearchResult): ManagedRuleResult {
  return {
    rule_id: result.ruleId,
    name: result.name,
    description: result.description,
    train_types: result.trainTypes || [],
    training_count: result.trainingCount,
    playbook_id: result.playbookId,
    playbook_name: result.playbookName,
    sample_edits: result.sampleEdits || [],
    sample_sentences: result.sampleSentences || [],
  };
}

interface Props {
  initialQuery: string;
  excludePlaybookId: string;
  onCopy: (ruleId: string, playbookName: string) => void;
  disabled?: boolean;
}

export function ManualSearchPanel({ initialQuery, excludePlaybookId, onCopy, disabled }: Props) {
  const { bbAxios } = useFtueHost();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<Array<RuleSearchResult>>([]);
  const [isSearching, setIsSearching] = useState(false);

  const doSearch = useCallback(
    async (q: string) => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const found = await searchRules(bbAxios, q, excludePlaybookId);
        setResults(found);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [bbAxios, excludePlaybookId]
  );

  // Debounced search on query change
  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 400);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  return (
    <Box borderWidth="1px" borderRadius="md" p="3" bg="bg.surface" mt="2">
      <Input
        placeholder="Search rules from other playbooks..."
        value={query}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
        size="sm"
      />
      {isSearching && (
        <Flex.H justify="center" mt="2">
          <Spinner size="sm" />
        </Flex.H>
      )}
      {!isSearching && results.length > 0 && (
        <RuleSearchResults
          results={results.slice(0, 5).map(convertToManagedResult)}
          onSelect={(rule) => {
            const original = results.find((r) => r.ruleId === rule.rule_id);
            // biome-ignore lint/style/useBlockStatements: single statement is acceptable here
            if (original) onCopy(original.ruleId, original.playbookName);
          }}
          disabled={disabled}
        />
      )}
    </Box>
  );
}
